import { useTranslation } from "react-i18next";
import { LogIn } from "lucide-react";

import { cn } from "@/engine/support/classNames";

/**
 * Hidden where the head found a stored session (`storedSession.ts`): the
 * first visit is drawn on the first paint for everybody else, and a signed-in
 * visitor never sees it arrive and leave (#872).
 */
export const WHERE_A_SESSION_IS_STORED_HIDDEN =
  "[html[data-stored-session]_&]:hidden";

/**
 * What a first visit meets instead of a list it does not have: where its rooms
 * will be, that joining friends needs no account, and an account offered for
 * what it keeps. The offer is an outline, never the filled control — the one
 * amber press on this screen is still the way into a room.
 *
 * A plain anchor rather than a router link: the entry screen renders outside a
 * router in its tests and stories.
 */
export function EntryWelcome({
  shown,
  provisional = false,
}: {
  shown: boolean;
  /** The account has not answered; a stored session hides this meanwhile. */
  provisional?: boolean;
}) {
  const { t } = useTranslation();
  if (!shown) return null;
  return (
    <section
      data-testid="entry-welcome"
      data-provisional={provisional || undefined}
      className={cn(
        "mb-4 flex flex-col gap-4",
        provisional && WHERE_A_SESSION_IS_STORED_HIDDEN,
      )}
    >
      <h2 className="text-xl font-semibold tracking-tight text-foreground">
        {t("entry.welcome.title")}
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col justify-center gap-2 rounded-2xl border border-dashed border-border p-4">
          <p className="font-semibold text-foreground">
            {t("entry.welcome.roomsTitle")}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("entry.welcome.roomsBody")}
          </p>
        </div>
        <div className="flex flex-col justify-between gap-3 rounded-2xl border border-primary/35 bg-primary/8 p-4">
          <div className="flex flex-col gap-2">
            <p className="font-semibold text-foreground">
              {t("entry.welcome.keepTitle")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("entry.welcome.keepBody")}
            </p>
          </div>
          <a
            href="/auth"
            data-testid="entry-welcome-sign-in"
            className="inline-flex min-h-11 items-center gap-2 self-start rounded-xl border border-border px-4 text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <LogIn className="size-4" aria-hidden="true" />
            {t("entry.welcome.signIn")}
          </a>
        </div>
      </div>
    </section>
  );
}
